import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, img, money } from "@/lib/api";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow, MaskedLine } from "@/components/Reveal";

const FRAMES = [
  { src: "lookbook-1.jpg", caption: "Frame 01 — Night Shift", cls: "md:col-span-7 aspect-[4/5]" },
  { src: "hero-v5.jpg", caption: "Frame 02 — The Standard", cls: "md:col-span-5 aspect-[3/4] md:mt-24",
    spots: [{ x: 50, y: 55, slug: "panther-panels-long-sleeve" }] },
  { src: "lookbook-3.jpg", caption: "Frame 03 — Everglades Dusk", cls: "md:col-span-5 aspect-[3/4]" },
  { src: "lookbook-2.jpg", caption: "Frame 04 — Back to Back", cls: "md:col-span-7 aspect-[4/5] md:-mt-24" },
  { src: "story.jpg", caption: "Frame 05 — The Atelier", cls: "md:col-span-12 aspect-[21/9]" },
];

export default function Lookbook() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-16" data-testid="lookbook-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16">
        <Reveal><Eyebrow>Drop 001 — Documented</Eyebrow></Reveal>
        <h1 className="font-display font-extrabold uppercase tracking-tight text-5xl sm:text-7xl mt-4 leading-[0.9]">
          <MaskedLine delay={0.1}><span className="text-cream">Look</span></MaskedLine>
          <MaskedLine delay={0.2}><span className="text-outline">book</span></MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-6 text-cream/50 text-sm max-w-lg">
            Shot after dark, between the hours most people waste. This isn't marketing — it's documentation.
            <span className="text-gold"> Tap the gold dots to shop the look.</span>
          </p>
        </Reveal>
      </div>

      <Marquee className="mb-16" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-32 grid grid-cols-1 md:grid-cols-12 gap-6">
        {FRAMES.map((f, i) => (
          <Reveal key={f.src + i} className={f.cls} delay={0.05}>
            <figure className="group relative w-full h-full overflow-visible bg-noir-card" data-testid={`lookbook-frame-${i + 1}`}>
              <div className="w-full h-full overflow-hidden">
                <img src={img(f.src)} alt={f.caption} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.6s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]" />
              </div>
              <figcaption className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-noir-bg/80 to-transparent pointer-events-none">
                <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold/90">{f.caption}</span>
              </figcaption>
              {f.spots?.map((s, si) => {
                const p = products.find((x) => x.slug === s.slug);
                const id = `${i}-${si}`;
                return (
                  <div key={si} className="absolute z-10" style={{ left: `${s.x}%`, top: `${s.y}%` }}>
                    <button
                      onClick={() => setActive(active === id ? null : id)}
                      className="relative block w-9 h-9 -translate-x-1/2 -translate-y-1/2"
                      data-testid={`lookbook-hotspot-${s.slug}`}
                      aria-label={`Shop ${p?.name || "this piece"}`}
                    >
                      <span className="absolute inset-0 rounded-full bg-gold/40 animate-ping" />
                      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 block h-3 w-3 rounded-full bg-gold border border-noir-bg shadow-[0_0_12px_rgba(212,175,55,0.8)]" />
                    </button>
                    {active === id && p && (
                      <Link
                        to={`/product/${p.slug}`}
                        className="absolute left-0 -translate-x-1/2 top-4 z-20 block w-44 bg-noir-panel border border-gold/40 p-3 shadow-2xl"
                        data-testid={`lookbook-hotspot-card-${s.slug}`}
                      >
                        <div className="aspect-square overflow-hidden bg-noir-card mb-2">
                          <img src={img(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
                        </div>
                        <p className="font-display text-[11px] font-bold uppercase leading-tight">{p.name}</p>
                        <div className="flex justify-between items-center mt-1.5">
                          <span className="font-mono2 text-[10px] text-gold">{money(p.price)}</span>
                          <span className="font-mono2 text-[8px] tracking-[0.2em] uppercase text-cream/50">Shop →</span>
                        </div>
                      </Link>
                    )}
                  </div>
                );
              })}
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
